import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  Grid,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { PilotProps } from "../types/view-pilot";

interface PilotOnboardingProps {
  pilot: PilotProps | null;
}

export const PilotOnboarding: React.FC<PilotOnboardingProps> = ({ pilot }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!pilot) return null;

  const filledFields = pilot.crewFields?.filled_fields ?? [];
  const nullFields = pilot.crewFields?.null_fields ?? [];
  const completionPct = pilot.completeness_score ?? 0;
  const totalPoints = [...filledFields, ...nullFields].reduce(
    (sum, field) => sum + (field.points ?? 0),
    0,
  );
  const filledPoints = filledFields.reduce(
    (sum, field) => sum + (field.points ?? 0),
    0,
  );
  const normalizedPct = totalPoints > 0 ? Math.round((filledPoints / totalPoints) * 100) : completionPct;

  const displayPct = Number.isFinite(normalizedPct) ? normalizedPct : completionPct;

  return (
    <Box sx={{ p: 1, minHeight: "auto" }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Card sx={{ boxShadow: 0, borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Onboarding Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track completed profile fields and onboarding tasks.
                  </Typography>
                </Box>
                <Chip
                  label={`${displayPct}% Complete`}
                  color={displayPct >= 100 ? "success" : displayPct >= 60 ? "warning" : "default"}
                />
              </Box>

              <Box mb={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Completion
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filledPoints}/{totalPoints} points
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={displayPct}
                  sx={{ height: 12, borderRadius: 6, bgcolor: colors.primary[800] }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Completed Fields
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {filledFields.length} fields completed
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {filledFields.map((field) => (
                      <Box
                        key={field.field}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: colors.primary[900],
                        }}
                      >
                        <Typography variant="body2">{field.label}</Typography>
                        <Chip label={`+${field.points}`} size="small" color="success" />
                      </Box>
                    ))}
                    {filledFields.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No completed onboarding fields yet.
                      </Typography>
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Remaining Fields
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {nullFields.length} fields remaining
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {nullFields.map((field) => (
                      <Box
                        key={field.field}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: colors.primary[900],
                        }}
                      >
                        <Typography variant="body2">{field.label}</Typography>
                        <Chip label={`${field.points}`} size="small" />
                      </Box>
                    ))}
                    {nullFields.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No remaining onboarding fields.
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
